// 🛟 B369 (AI_156) — TELA BRANCA NUNCA MAIS SEM MENSAGEM
// ─────────────────────────────────────────────────────────────
// O app não tinha NENHUM error boundary. Qualquer exceção durante a
// renderização fazia o React desmontar a árvore inteira: `#root` com zero
// filhos, corpo vazio, nenhuma mensagem. Foi o que Leonardo viu ao pedir o
// laudo de Situação Especial em 29-30/10/2026 — e, sem mensagem, nem ele nem
// eu tínhamos como saber o que quebrou.
//
// Duas coisas aqui, e as duas importam:
//  1. O médico vê um aviso em vez de um vazio, sabe que o registro no servidor
//     está íntegro, e tem um botão para copiar o detalhe e me mandar.
//  2. O erro fica GRAVADO no navegador (sobrevive ao recarregamento, que é o
//     que apaga qualquer captura feita ao vivo) — sem dado de paciente: só
//     mensagem, pilha e a versão do app.
//
// LGPD: a pilha de erro pode citar nomes de função e arquivo, nunca conteúdo
// clínico. Nada é enviado a ninguém: fica no navegador até você copiar.
// ─────────────────────────────────────────────────────────────
import React from "react";

const CHAVE = "hepato_erro_tela";
const MAX = 5;

export type ErroGravado = {
  quando: string;
  origem: "render" | "janela" | "promessa";
  mensagem: string;
  pilha?: string;
  componente?: string;
  rota?: string;
  versao?: string;
};

export function gravarErro(erro: Omit<ErroGravado, "quando">): void {
  try {
    const lista: ErroGravado[] = JSON.parse(localStorage.getItem(CHAVE) || "[]");
    lista.push({
      quando: new Date().toISOString(),
      ...erro,
      mensagem: String(erro.mensagem || "").slice(0, 600),
      pilha: String(erro.pilha || "").slice(0, 3000),
      componente: String(erro.componente || "").slice(0, 2000),
    });
    localStorage.setItem(CHAVE, JSON.stringify(lista.slice(-MAX)));
  } catch {
    // localStorage pode estar bloqueado — não é motivo para falhar de novo.
  }
}

export function errosGravados(): ErroGravado[] {
  try {
    const l = JSON.parse(localStorage.getItem(CHAVE) || "[]");
    return Array.isArray(l) ? l : [];
  } catch {
    return [];
  }
}

export function limparErrosGravados(): void {
  try {
    localStorage.removeItem(CHAVE);
  } catch {
    /* idem */
  }
}

/** Erros que escapam fora da renderização (async, promessa) também ficam gravados. */
export function instalarCapturaGlobal(versao?: string): void {
  if (typeof window === "undefined") return;
  window.addEventListener("error", (ev) => {
    gravarErro({
      origem: "janela",
      mensagem: ev.message || "erro sem mensagem",
      pilha: (ev.error && ev.error.stack) || "",
      rota: window.location.pathname,
      versao,
    });
  });
  window.addEventListener("unhandledrejection", (ev) => {
    const r: any = ev.reason;
    gravarErro({
      origem: "promessa",
      mensagem: (r && r.message) || String(r),
      pilha: (r && r.stack) || "",
      rota: window.location.pathname,
      versao,
    });
  });
}

type Props = { children: React.ReactNode; versao?: string };
type State = { erro: Error | null; componente: string };

export class LimiteDeErro extends React.Component<Props, State> {
  state: State = { erro: null, componente: "" };

  static getDerivedStateFromError(erro: Error): Partial<State> {
    return { erro };
  }

  componentDidCatch(erro: Error, info: React.ErrorInfo) {
    const componente = info?.componentStack || "";
    this.setState({ componente });
    // eslint-disable-next-line no-console
    console.error("🛟 [B369] tela caiu:", erro, componente);
    gravarErro({
      origem: "render",
      mensagem: erro?.message || String(erro),
      pilha: erro?.stack || "",
      componente,
      rota: window.location.pathname,
      versao: this.props.versao,
    });
  }

  private detalhe(): string {
    const { erro, componente } = this.state;
    return [
      `hepato.app — falha de tela`,
      `quando: ${new Date().toLocaleString("pt-BR")}`,
      `versao: ${this.props.versao || "(desconhecida)"}`,
      `rota: ${window.location.pathname}`,
      `erro: ${erro?.message || String(erro)}`,
      ``,
      `pilha:`,
      erro?.stack || "(sem pilha)",
      ``,
      `componentes:`,
      componente || "(sem árvore)",
    ].join("\n");
  }

  render() {
    if (!this.state.erro) return this.props.children;
    return (
      <div
        style={{
          maxWidth: 680,
          margin: "48px auto",
          padding: 24,
          fontFamily: "system-ui, -apple-system, sans-serif",
          border: "1px solid #fca5a5",
          borderRadius: 12,
          background: "#fef2f2",
          color: "#1f2937",
        }}
      >
        <h2 style={{ margin: "0 0 8px", fontSize: 20, color: "#b91c1c" }}>
          A tela falhou
        </h2>
        <p style={{ margin: "0 0 12px", lineHeight: 1.5 }}>
          Algo quebrou ao desenhar esta parte do prontuário.{" "}
          <strong>Nada foi perdido no servidor</strong> — o registro do paciente
          está íntegro. Recarregue a página para continuar.
        </p>
        <p style={{ margin: "0 0 16px", lineHeight: 1.5 }}>
          Se isso se repete, clique em <strong>Copiar detalhes</strong> e me
          mande o texto: ele diz exatamente o que quebrou.
        </p>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button
            onClick={() => {
              const txt = this.detalhe();
              const ok = () => window.alert("Detalhes copiados.");
              if (navigator.clipboard?.writeText) {
                navigator.clipboard.writeText(txt).then(ok, () =>
                  window.prompt("Copie o texto abaixo:", txt)
                );
              } else {
                window.prompt("Copie o texto abaixo:", txt);
              }
            }}
            style={{
              padding: "10px 16px",
              borderRadius: 8,
              border: "1px solid #b91c1c",
              background: "#b91c1c",
              color: "#fff",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Copiar detalhes
          </button>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: "10px 16px",
              borderRadius: 8,
              border: "1px solid #9ca3af",
              background: "#fff",
              color: "#374151",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Recarregar
          </button>
        </div>
        <pre
          style={{
            marginTop: 16,
            padding: 12,
            background: "#fff",
            border: "1px solid #e5e7eb",
            borderRadius: 8,
            fontSize: 11,
            lineHeight: 1.4,
            maxHeight: 220,
            overflow: "auto",
            whiteSpace: "pre-wrap",
          }}
        >
          {this.state.erro?.message || String(this.state.erro)}
          {"\n"}
          {this.state.erro?.stack || ""}
        </pre>
      </div>
    );
  }
}

export default LimiteDeErro;
